import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, AlertTriangle, Loader2, Link2, Trash2, FlaskConical } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/hooks/useFetch';
import { devLog } from '@/utils/devLog';

const NotionSettingsTab = () => {
  const [config, setConfig] = useState(null);
  const [blogsParent, setBlogsParent] = useState('');
  const [socialParent, setSocialParent] = useState('');
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await apiFetch('/api/admin/notion/config');
    setLoading(false);
    if (error) return toast.error(error);
    setConfig(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const setup = async () => {
    if (!blogsParent.trim() && !socialParent.trim()) {
      return toast.error('Paste at least one parent page URL');
    }
    setWorking(true);
    const { error } = await apiFetch('/api/admin/notion/setup', {
      method: 'POST',
      body: {
        blogs_parent: blogsParent.trim() || null,
        social_parent: socialParent.trim() || null,
      },
    });
    setWorking(false);
    if (error) return toast.error(error);
    toast.success('Notion databases created');
    setBlogsParent('');
    setSocialParent('');
    load();
  };

  const testPush = async () => {
    setTesting(true);
    const { data, error } = await apiFetch('/api/admin/notion/test', { method: 'POST' });
    setTesting(false);
    if (error) return toast.error(error);
    toast.success('Test rows created in Notion — check your workspace!');
    if (data?.blog_url) devLog('Blog test:', data.blog_url);
  };

  const reset = async () => {
    if (!window.confirm('Clear stored Notion database IDs? Existing Notion pages will NOT be deleted — you just lose the link from the admin.')) return;
    const { error } = await apiFetch('/api/admin/notion/config', { method: 'DELETE' });
    if (error) return toast.error(error);
    toast.success('Notion config cleared');
    load();
  };

  return (
    <div className="space-y-4" data-testid="notion-settings-tab">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-[#00A896]" /> Notion Integration
          </CardTitle>
          <CardDescription>
            Push blog posts (on publish/schedule) and social media drafts (on generate) into Notion databases for your workflow.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  {config?.has_token ? (
                    <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle2 className="h-3 w-3 mr-1" /> Token loaded</Badge>
                  ) : (
                    <Badge variant="destructive"><AlertTriangle className="h-3 w-3 mr-1" /> No token in env</Badge>
                  )}
                  <span className="text-slate-500">NOTION_API_KEY</span>
                </div>
                <div className="flex items-center gap-2">
                  {config?.configured ? (
                    <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle2 className="h-3 w-3 mr-1" /> Databases linked</Badge>
                  ) : (
                    <Badge variant="secondary">Not yet linked</Badge>
                  )}
                </div>
              </div>

              {!config?.configured ? (
                <div className="border-l-4 border-[#00A896] pl-4 py-2 bg-[#00E5CC]/5 rounded space-y-3">
                  <p className="text-sm font-medium">Step-by-step setup</p>
                  <ol className="text-sm text-slate-700 list-decimal pl-5 space-y-1">
                    <li>Open Notion → pick (or create) the pages where your blog and social media databases should live.</li>
                    <li>For each page, click <code className="bg-white dark:bg-slate-800 dark:text-slate-100 px-1 rounded text-xs">⋯ menu → Add connections</code> and share it with your ClearCommunicationApp integration.</li>
                    <li>Copy each page URL and paste below. You can use the same page for both, or two separate pages.</li>
                  </ol>
                  <div>
                    <Label htmlFor="notion-blogs-parent" className="text-sm">Blogs parent page URL</Label>
                    <Input
                      id="notion-blogs-parent"
                      placeholder="https://www.notion.so/Blog-Content-Management-49871c08..."
                      value={blogsParent}
                      onChange={(e) => setBlogsParent(e.target.value)}
                      data-testid="notion-blogs-parent-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="notion-social-parent" className="text-sm">Social Media parent page URL</Label>
                    <Input
                      id="notion-social-parent"
                      placeholder="https://www.notion.so/Social-Media-Content-Planner-c1171c08..."
                      value={socialParent}
                      onChange={(e) => setSocialParent(e.target.value)}
                      data-testid="notion-social-parent-input"
                    />
                  </div>
                  <Button
                    onClick={setup}
                    disabled={working || (!blogsParent.trim() && !socialParent.trim())}
                    className="bg-[#00E5CC] text-[#0d1f2d] hover:bg-[#00d4bd]"
                    data-testid="notion-setup-btn"
                  >
                    {working ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />}
                    Create databases
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 gap-2 text-xs font-mono bg-slate-50 dark:bg-slate-800 p-3 rounded">
                    <div><span className="text-slate-500 dark:text-slate-400">Blogs parent:</span> <span className="text-foreground">{config.blogs_parent_id || '—'}</span></div>
                    <div><span className="text-slate-500 dark:text-slate-400">Blogs DB:</span> <span className="text-foreground">{config.blogs_db_id}</span></div>
                    <div><span className="text-slate-500 dark:text-slate-400">Social parent:</span> <span className="text-foreground">{config.social_parent_id || '—'}</span></div>
                    <div><span className="text-slate-500 dark:text-slate-400">Social DB:</span> <span className="text-foreground">{config.social_db_id}</span></div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={testPush}
                      disabled={testing}
                      data-testid="notion-test-btn"
                    >
                      {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FlaskConical className="h-4 w-4 mr-2" />}
                      Send test rows
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={reset}
                      className="text-red-600"
                      data-testid="notion-reset-btn"
                    >
                      <Trash2 className="h-4 w-4 mr-2" /> Disconnect
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    New blog posts and social drafts will sync automatically. Existing rows in Notion are NOT deleted on disconnect.
                  </p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NotionSettingsTab;
